import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { message } from 'antd';

import { extractPixelSpacingFromDicomFile } from '../../../helpers/dicom/extractPixelSpacing';
import { createQuickInferencesBatch } from '../../../helpers/api/quick-inference/quick-inference';
import { fileKind } from './quickInferenceUtils';

const UPLOAD_CONCURRENCY = 3;

export type UploadQueueStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadFileStatus {
  uid: string;
  name: string;
  size: number;
  status: UploadQueueStatus;
  error?: string;
}

interface LiveQueueItem extends UploadFileStatus {
  file: File;
  progress: number;
}

interface DicomSpacingInfo {
  uid: string;
  file: File;
  spacing: number | null;
  fromDicom: boolean;
}

function fileUid(file: File, index: number): string {
  return `${file.name}-${file.size}-${index}`;
}

function recomputeOverallProgress(queue: LiveQueueItem[]): number {
  if (!queue.length) return 0;
  const sum = queue.reduce((acc, item) => acc + item.progress, 0);
  return Math.min(100, Math.round(sum / queue.length));
}

function toPublicStatus(item: LiveQueueItem): UploadFileStatus {
  return {
    uid: item.uid,
    name: item.name,
    size: item.size,
    status: item.status,
    error: item.error,
  };
}

export function useQuickInferenceUpload(onSuccess: (sessionId: string) => void) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [dicomSpacingInfo, setDicomSpacingInfo] = useState<DicomSpacingInfo[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileStatuses, setUploadFileStatuses] = useState<UploadFileStatus[]>([]);
  const [activeFileName, setActiveFileName] = useState<string | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [parsingDicom, setParsingDicom] = useState(false);

  const selectedFiles = useMemo(
    () =>
      fileList
        .map((f) => f.originFileObj as File | undefined)
        .filter((f): f is File => !!f),
    [fileList]
  );

  const resetSelection = useCallback(() => {
    setFileList([]);
    setDicomSpacingInfo([]);
    setUploadProgress(0);
    setUploadFileStatuses([]);
    setActiveFileName(null);
    setParsingDicom(false);
    setCompletedCount(0);
  }, []);

  const parseDicomSpacing = useCallback(async (files: File[]) => {
    const dicomFiles = files.filter((f) => fileKind(f) === 'dicom');
    if (!dicomFiles.length) {
      setDicomSpacingInfo([]);
      return;
    }

    setParsingDicom(true);
    try {
      const info: DicomSpacingInfo[] = [];
      for (let i = 0; i < dicomFiles.length; i++) {
        const file = dicomFiles[i];
        const extracted = await extractPixelSpacingFromDicomFile(file);
        info.push({
          uid: fileUid(file, files.indexOf(file)),
          file,
          spacing: extracted.value,
          fromDicom: extracted.source === 'dicom',
        });
      }
      setDicomSpacingInfo(info);
    } finally {
      setParsingDicom(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedFiles.length) {
      setDicomSpacingInfo([]);
      return;
    }
    parseDicomSpacing(selectedFiles);
  }, [selectedFiles, parseDicomSpacing]);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const next = Array.from(incoming);
    if (!next.length) return;

    setFileList((prev) => {
      const existing = new Set(
        prev
          .map((f) => f.originFileObj as File | undefined)
          .filter((f): f is File => !!f)
          .map((f) => `${f.name}-${f.size}`)
      );

      const merged = [...prev];
      next.forEach((file, i) => {
        const key = `${file.name}-${file.size}`;
        if (existing.has(key)) return;
        existing.add(key);
        merged.push({
          uid: fileUid(file, merged.length + i),
          name: file.name,
          status: 'done',
          originFileObj: file,
        });
      });
      return merged;
    });
  }, []);

  const removeFileAt = useCallback((index: number) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedFiles.length) {
      message.warning('Please choose at least one file');
      return;
    }

    const liveQueue: LiveQueueItem[] = selectedFiles.map((file, i) => ({
      uid: fileUid(file, i),
      file,
      name: file.name,
      size: file.size,
      status: 'queued' as const,
      progress: 0,
    }));

    setUploadFileStatuses(liveQueue.map(toPublicStatus));
    setSubmitting(true);
    setUploadProgress(0);
    setCompletedCount(0);
    setActiveFileName(null);

    let rafId: number | null = null;
    let lastPaintedProgress = -1;
    let statusesDirty = false;

    const paintProgress = () => {
      rafId = null;
      const nextProgress = recomputeOverallProgress(liveQueue);
      if (nextProgress !== lastPaintedProgress) {
        lastPaintedProgress = nextProgress;
        setUploadProgress(nextProgress);
      }
      const active = liveQueue.find((item) => item.status === 'uploading');
      setActiveFileName(active?.name ?? null);
      setCompletedCount(
        liveQueue.filter((item) => item.status === 'done' || item.status === 'error').length
      );
      if (statusesDirty) {
        statusesDirty = false;
        setUploadFileStatuses(liveQueue.map(toPublicStatus));
      }
    };

    const scheduleProgressPaint = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(paintProgress);
    };

    const markStatusesDirty = () => {
      statusesDirty = true;
      scheduleProgressPaint();
    };

    try {
      const { sessionId, results } = await createQuickInferencesBatch({
        files: selectedFiles,
        concurrency: UPLOAD_CONCURRENCY,
        getPixelSpacingForFile: (file) => {
          const kind = fileKind(file);
          if (kind === 'dicom') {
            const info = dicomSpacingInfo.find((s) => s.file === file);
            if (info?.fromDicom && info.spacing != null && info.spacing > 0) {
              return { pixelSpacing: info.spacing, pixelSpacingUnit: 'mm' as const };
            }
            return { pixelSpacing: -1, pixelSpacingUnit: 'mm' as const };
          }
          return { pixelSpacing: -1, pixelSpacingUnit: 'mm' as const };
        },
        onFileStart: (_file, index) => {
          liveQueue[index] = {
            ...liveQueue[index],
            status: 'uploading',
            progress: Math.max(liveQueue[index].progress, 3),
          };
          markStatusesDirty();
        },
        onFileUploadProgress: (_file, index, loaded, total) => {
          const pct = total > 0 ? Math.min(97, Math.round((loaded / total) * 100)) : 0;
          liveQueue[index] = {
            ...liveQueue[index],
            status: 'uploading',
            progress: pct,
          };
          scheduleProgressPaint();
        },
        onProgress: (_done, _total, _file, result, index) => {
          if ('error' in result) {
            liveQueue[index] = {
              ...liveQueue[index],
              status: 'error',
              progress: 100,
              error: result.error,
            };
          } else {
            liveQueue[index] = {
              ...liveQueue[index],
              status: 'done',
              progress: 100,
            };
          }
          markStatusesDirty();
        },
      });

      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      paintProgress();

      const ok = results.filter((r) => r.id).length;
      const fail = results.length - ok;
      if (ok) {
        message.success(
          fail
            ? `Session created: ${ok} queued, ${fail} failed`
            : `Session created with ${ok} file${ok === 1 ? '' : 's'} queued`
        );
        resetSelection();
        onSuccess(sessionId);
      } else {
        message.error('All uploads failed');
      }
    } catch (err: any) {
      message.error(err?.message || 'Upload failed');
    } finally {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      setSubmitting(false);
      setActiveFileName(null);
    }
  }, [selectedFiles, dicomSpacingInfo, onSuccess, resetSelection]);

  return {
    fileList,
    setFileList,
    selectedFiles,
    dicomSpacingInfo,
    submitting,
    uploadProgress,
    uploadFileStatuses,
    activeFileName,
    completedCount,
    parsingDicom,
    addFiles,
    removeFileAt,
    resetSelection,
    handleSubmit,
    fileUid,
  };
}
